import { notify } from '@affine/component';
import {
  type WorkspaceMetadata,
  WorkspacesService,
} from '@affine/core/modules/workspace';
import { UNTITLED_WORKSPACE_NAME } from '@affine/env/constant';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { type SyntheticEvent, useCallback, useRef, useState } from 'react';

import { useWorkspaceInfo } from './use-workspace-info';

export const useDuplicateWorkspace = (metadata: WorkspaceMetadata) => {
  const t = useI18n();
  const workspacesService = useService(WorkspacesService);
  const workspaceInfo = useWorkspaceInfo(metadata);
  const [duplicating, setDuplicating] = useState(false);
  const duplicatingRef = useRef(false);

  const duplicateAsync = useCallback(
    async (event?: SyntheticEvent) => {
      event?.stopPropagation();
      if (duplicatingRef.current) {
        return;
      }

      duplicatingRef.current = true;
      setDuplicating(true);
      const sourceName = workspaceInfo?.name ?? UNTITLED_WORKSPACE_NAME;
      const copyName = `${sourceName} — ${t['Copy']()}`;

      try {
        await workspacesService.duplicateWorkspace(metadata, copyName);
        notify.success({
          title: t['com.affine.workspace.duplicate.success'](),
          message: copyName,
        });
      } catch (error) {
        notify.error({
          title: t['com.affine.workspace.duplicate.failed'](),
          message: error instanceof Error ? error.message : String(error),
        });
      } finally {
        duplicatingRef.current = false;
        setDuplicating(false);
      }
    },
    [metadata, t, workspaceInfo?.name, workspacesService]
  );

  const duplicate = useCallback(
    (event?: SyntheticEvent) => {
      duplicateAsync(event).catch(error => {
        console.error('Unexpected error while duplicating workspace', error);
      });
    },
    [duplicateAsync]
  );

  return { duplicate, duplicating };
};
